import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';
import { MouseEvent, ReactNode } from 'react';

import { ButtonProps } from '../jdsbutton/logic';
import { JDSModalMobileProps } from './generated';
import { ListBlockProps } from '../jdslistblock/logic';
import { convertTextToComponent } from '../../utils/componenthelpers';
import { useEscapeClose } from '../../hooks/useEscapeClose';

const legacySizes = ['s', 'm'] as const;
type legacySizesType = (typeof legacySizes)[number];

export interface ModalProps
  extends Omit<
    JDSModalMobileProps,
    'size' | 'primaryCTA' | 'secondaryCTA' | 'header' | '_onModalClick'
  > {
  /**
   * Sets size of Modal
   */
  size?: legacySizesType | JDSModalMobileProps['size'];
  /**
   * Set type of modal, informational modal has no buttons,
   * dialog modal has only a single button which can be triggered by
   * clicking Enter key
   */
  kind?: 'dialog' | 'acknowledgement' | 'informational';
  /**
   * Sets Header as text or as react element
   */
  header?: string | ListBlockProps;
  /**
   * Sets Modal content as a children elements eg. `<Modal> <Table /> <Modal/>`
   */
  children?: ReactNode;
  /**
   * Label of primary button. You can also pass a custom LinkType for more granular control. <br/>
   * E.g. `primaryCTA={title:'Delete', {button: { state: 'destructive' } }}`
   */
  primaryCTA?:
    | string
    | {
        title: string;
        onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
        /**
         * @deprecated
         */
        _button?: ButtonProps;
      };
  /**
   * Label of secondary button.You can also pass a custom LinkType for more granular control. <br/>
   * E.g. `secondaryCTA={title:'Delete', {button: { state: 'destructive' } }}`
   */
  secondaryCTA?:
    | string
    | {
        title: string;
        onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
        /**
         * @deprecated
         */
        _button?: ButtonProps;
      };
  /**
   * Onclick Event on primary button
   */
  onClickPrimary?: (
    event?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
  /**
   * Onclick Event on secondary button
   */
  onClickSecondary?: (
    event?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
  /**
   * Removes the [X] icon and shifts layout up
   * @deprecated
   * use `isCloseable` prop
   */
  isClosable?: boolean;
  /**
   * Can be set to close modal programatically
   * @deprecated use `close` prop
   */
  closed?: boolean;
  /**
   * Pass custom callback on closing modal
   */
  onCloseCallback?: () => void;
  /**
   * Function which determines if modal can be closed.
   * If it is false, the modal does not close if clicking on close button or esc
   */
  onRequestClose?: () => boolean;
}

export const PROP_MAP: IPROP_MAP<ModalProps> = (props) => {
  if (legacySizes.includes(props.size as legacySizesType)) {
    console.warn('these values for the size prop are deprecated');
  }
  if (typeof props.header === 'object') {
    console.warn(
      'passing listblock to the header is deprecated. Please pass a string'
    );
  }
  return {
    onCloseCallback: {
      properties: {
        onClose: props.onCloseCallback,
        onCloseCallback: props.onCloseCallback
      }
    },
    primaryCTA: {
      properties: {
        primaryCTA: props.primaryCTA?.title || props.primaryCTA,
        onPrimaryClick: props.primaryCTA?.onClick || props.onPrimaryClick
      }
    },
    secondaryCTA: {
      properties: {
        secondaryCTA: props.secondaryCTA?.title || props.secondaryCTA,
        onSecondaryClick: props.secondaryCTA?.onClick || props.onSecondaryClick
      }
    },
    isClosable: {
      properties: {
        isCloseable:
          typeof props.isCloseable === 'undefined'
            ? props.isClosable
            : props.isCloseable
      }
    },
    size: {
      variations: {
        s: {
          size: 'small'
        },
        m: {
          size: 'medium'
        }
      }
    },
    children: {
      properties: {
        children: convertTextToComponent(props.children)
      }
    }
  };
};

export const withLogic =
  (Component: React.ComponentType<ModalProps>): React.FC<ModalProps> =>
  (userProps: ModalProps) => {
    const props = modifyProps(userProps, PROP_MAP);
    const {
      onRequestClose,
      onCloseCallback,
      kind,
      isClosable,
      close: userCloseProp,
      closed,
      ...rest
    } = props;

    const closeProp =
      typeof userCloseProp === 'undefined' ? closed : userCloseProp;

    const allowClose = !onRequestClose || onRequestClose();
    const close = () => {
      if (allowClose) {
        onCloseCallback?.();
        rest.onClose?.();
      }
    };

    useEscapeClose(close, kind === 'informational');

    const handleClose = () => {
      if (kind === 'informational') {
        close();
      }
    };

    return (
      <Component
        {...{
          ...rest,
          kind,
          _onModalClick: (ev: React.MouseEvent<HTMLDivElement>) =>
            ev.stopPropagation(),
          close: closeProp,
          _onCancel: close,
          onClose: handleClose,
          isCloseable: isClosable || props.isCloseable
        }}
      />
    );
  };
